<template>
  <dialog
    :id="modalId"
    class="modal z-[9999]"
    :class="{ 'modal-open': isOpen }"
  >
    <div class="modal-box w-11/12 max-w-3xl rounded-[20px] bg-white text-black p-0 shadow-2xl overflow-hidden flex flex-col font-['Raleway']">
      <!-- Header -->
      <div class="px-8 pt-6 pb-4 flex items-center justify-between border-b border-gray-200">
        <div>
          <h3 class="font-bold text-[18px] text-[#043f42] m-0">
            Folio Signatories Required
          </h3>
          <p class="text-[13px] text-gray-500 m-0 mt-1">
            Associate recipient emails to the submitters you have mapped for this folio.
          </p>
        </div>
        <button
          type="button"
          class="text-gray-400 hover:text-gray-700 text-2xl font-light leading-none border-none bg-transparent cursor-pointer p-1"
          @click="$emit('close')"
        >
          &times;
        </button>
      </div>

      <!-- Warning Banner -->
      <div class="px-8 py-3 bg-[#fffbeb] border-b border-[#fef3c7] flex items-center gap-3">
        <span class="text-amber-600 text-lg">⚠️</span>
        <span class="text-[13px] text-amber-900 font-medium leading-snug">
          You need to add emails associated to the mappings, to the signatories, to the submitters that you have mapped for in this folio.
        </span>
      </div>

      <!-- Mode Selector Tabs -->
      <div class="px-8 pt-4 pb-2 flex gap-4 border-b border-gray-100">
        <button
          type="button"
          class="pb-2 text-[14px] font-bold border-b-2 transition-colors cursor-pointer bg-transparent"
          :class="activeTab === 'manual' ? 'border-[#01d680] text-[#043f42]' : 'border-transparent text-gray-400 hover:text-gray-600'"
          @click="activeTab = 'manual'"
        >
          Manual Entry ({{ localSubmitters.length }})
        </button>
        <button
          type="button"
          class="pb-2 text-[14px] font-bold border-b-2 transition-colors cursor-pointer bg-transparent"
          :class="activeTab === 'csv' ? 'border-[#01d680] text-[#043f42]' : 'border-transparent text-gray-400 hover:text-gray-600'"
          @click="activeTab = 'csv'"
        >
          Upload CSV
        </button>
      </div>

      <!-- Body Content -->
      <div class="px-8 py-4 overflow-y-auto max-h-[50vh] flex flex-col gap-4">
        <!-- Tab: Manual Entry -->
        <div
          v-if="activeTab === 'manual'"
          class="flex flex-col gap-3"
        >
          <div
            v-if="csvNotice"
            class="p-3 bg-[#ecfdf5] border border-[#a7f3d0] rounded-[8px] text-[13px] text-[#065f46] flex items-center justify-between"
          >
            <span>✅ {{ csvNotice }}</span>
            <button
              type="button"
              class="text-xs text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"
              @click="csvNotice = ''"
            >
              &times;
            </button>
          </div>

          <div class="flex flex-col gap-3">
            <div
              v-for="(sub, idx) in localSubmitters"
              :key="sub.uuid || idx"
              class="p-4 rounded-[12px] bg-[#f8fafc] border border-gray-200 flex flex-col gap-2"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full bg-[#01d680]" />
                  <span class="font-bold text-[14px] text-[#043f42]">
                    Signatory {{ idx + 1 }}
                    <span
                      v-if="sub.name"
                      class="text-gray-500 font-normal text-xs ml-1"
                    >({{ sub.name }})</span>
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">First Name</label>
                  <input
                    v-model="sub.first_name"
                    type="text"
                    placeholder="e.g. Alice"
                    class="w-full text-[13px] px-3 py-2 rounded-[6px] border border-gray-300 bg-white focus:border-[#01d680] focus:ring-1 focus:ring-[#01d680] outline-none"
                    @input="updateFullName(sub)"
                  >
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">Surname</label>
                  <input
                    v-model="sub.last_name"
                    type="text"
                    placeholder="e.g. Smith"
                    class="w-full text-[13px] px-3 py-2 rounded-[6px] border border-gray-300 bg-white focus:border-[#01d680] focus:ring-1 focus:ring-[#01d680] outline-none"
                    @input="updateFullName(sub)"
                  >
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-600 mb-1">
                    Email Address <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="sub.email"
                    type="email"
                    placeholder="alice@example.com"
                    required
                    class="w-full text-[13px] px-3 py-2 rounded-[6px] border bg-white focus:ring-1 outline-none"
                    :class="sub.email && isValidEmail(sub.email) ? 'border-gray-300 focus:border-[#01d680] focus:ring-[#01d680]' : 'border-red-400 focus:border-red-500 focus:ring-red-400'"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab: Upload CSV -->
        <div
          v-else-if="activeTab === 'csv'"
          class="flex flex-col gap-4 py-2"
        >
          <div class="p-6 rounded-[12px] border-2 border-dashed border-[#01d680] bg-[#f5fefe] flex flex-col items-center justify-center text-center">
            <span class="text-[#01d680] text-3xl mb-2">📄</span>
            <p class="font-bold text-[14px] text-[#043f42] m-0 mb-1">
              Select or drop your Signatories CSV file
            </p>
            <p class="text-xs text-gray-500 m-0 mb-4 max-w-sm">
              Columns expected: <strong>Name, Surname, Email</strong> (or student_name, student_surname, email).
            </p>
            <label class="h-[40px] px-6 rounded-[20px] bg-[#043f42] text-white font-bold text-[13px] flex items-center justify-center cursor-pointer hover:bg-[#065b5f] transition-colors">
              <span>Browse CSV</span>
              <input
                type="file"
                accept=".csv,.txt"
                class="hidden"
                @change="handleCsvUpload"
              >
            </label>
          </div>

          <div
            v-if="csvError"
            class="p-3 bg-red-50 border border-red-200 rounded-[8px] text-xs text-red-600"
          >
            {{ csvError }}
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
        <div class="text-xs text-gray-500">
          <span
            v-if="allValid"
            class="text-[#01d680] font-bold"
          >✓ All {{ localSubmitters.length }} signatories ready</span>
          <span
            v-else
            class="text-red-500 font-medium"
          >⚠️ Please provide valid emails for all {{ localSubmitters.length }} signatories</span>
        </div>
        <div class="flex gap-3">
          <button
            type="button"
            class="px-5 py-2.5 rounded-[24px] border border-gray-300 bg-white text-gray-700 font-bold text-[13px] hover:bg-gray-100 transition-colors cursor-pointer"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="!allValid"
            class="px-6 py-2.5 rounded-[24px] bg-[#01d680] text-black font-bold text-[13px] hover:bg-[#00c576] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-none"
            @click="submitSignatories"
          >
            Save Signatories
          </button>
        </div>
      </div>
    </div>
  </dialog>
</template>

<script>
import { v4 } from 'uuid'

export default {
  name: 'FolioSignatoriesModal',
  props: {
    submitters: {
      type: Array,
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    modalId: {
      type: String,
      default: 'folio-signatories-modal'
    }
  },
  emits: ['close', 'save-signatories'],
  data () {
    return {
      activeTab: 'manual',
      localSubmitters: [],
      csvNotice: '',
      csvError: ''
    }
  },
  computed: {
    allValid () {
      if (!this.localSubmitters.length) return false
      return this.localSubmitters.every((s) => s.email && this.isValidEmail(s.email.trim()))
    }
  },
  watch: {
    submitters: {
      immediate: true,
      deep: true,
      handler (newVal) {
        this.initializeLocalSubmitters(newVal)
      }
    },
    isOpen (open) {
      if (open) {
        this.initializeLocalSubmitters(this.submitters)
        this.activeTab = 'manual'
        this.csvNotice = ''
        this.csvError = ''
      }
    }
  },
  methods: {
    initializeLocalSubmitters (submitters) {
      this.localSubmitters = (submitters || []).map((s) => {
        let firstName = s.first_name || ''
        let lastName = s.last_name || ''
        if (!firstName && !lastName && s.name && !s.name.toLowerCase().includes('party')) {
          const parts = s.name.trim().split(/\s+/)
          firstName = parts[0] || ''
          lastName = parts.slice(1).join(' ') || ''
        }
        return {
          uuid: s.uuid,
          name: s.name || '',
          first_name: firstName,
          last_name: lastName,
          email: s.email || '',
          order: s.order
        }
      })
    },
    isValidEmail (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    updateFullName (sub) {
      const parts = [sub.first_name, sub.last_name].filter(Boolean)
      if (parts.length > 0) {
        sub.name = parts.join(' ')
      }
    },
    handleCsvUpload (e) {
      this.csvError = ''
      const file = e.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const text = event.target.result
          const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim().length > 0)
          if (lines.length < 2) {
            this.csvError = 'CSV file must have at least a header and one row of data.'
            return
          }

          const delimiter = lines[0].includes(';') ? ';' : (lines[0].includes('\t') ? '\t' : ',')
          const headers = lines[0].split(delimiter).map((h) => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'))

          const nameIdx = headers.findIndex((h) => ['student_name', 'name', 'first_name', 'firstname'].includes(h))
          const surnameIdx = headers.findIndex((h) => ['student_surname', 'surname', 'last_name', 'lastname'].includes(h))
          const emailIdx = headers.findIndex((h) => ['email', 'email_address', 'student_email'].includes(h))

          const parsedRows = []
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(delimiter).map((c) => c.trim().replace(/^["']|["']$/g, ''))
            if (cols.every((c) => !c)) continue

            const firstName = nameIdx !== -1 ? cols[nameIdx] : cols[0] || ''
            const surname = surnameIdx !== -1 ? cols[surnameIdx] : cols[1] || ''
            const email = emailIdx !== -1 ? cols[emailIdx] : cols[2] || ''

            if (email && this.isValidEmail(email)) {
              parsedRows.push({
                first_name: firstName,
                last_name: surname,
                email,
                name: [firstName, surname].filter(Boolean).join(' ').trim() || email
              })
            }
          }

          if (parsedRows.length === 0) {
            this.csvError = 'No valid rows with email addresses found in the CSV.'
            return
          }

          // Preserve owner submitter (or first submitter if owner)
          const ownerSub = this.localSubmitters.find(s => s.role === 'owner') || this.localSubmitters[0] || {
            uuid: v4(),
            name: 'Signatory 1',
            role: 'owner',
            order: 0
          }
          const existingNonOwner = this.localSubmitters.filter(s => s !== ownerSub)

          const newSubmitters = [ownerSub]
          parsedRows.forEach((row, i) => {
            const existing = existingNonOwner[i]
            newSubmitters.push({
              uuid: existing?.uuid || v4(),
              name: row.name,
              first_name: row.first_name,
              last_name: row.last_name,
              email: row.email,
              order: (ownerSub.order != null ? ownerSub.order + 1 : 1) + i
            })
          })

          this.localSubmitters = newSubmitters
          this.csvNotice = `Successfully added ${parsedRows.length} signatories from CSV.`
          this.activeTab = 'manual'
        } catch (err) {
          this.csvError = `Failed to parse CSV: ${err.message}`
        }
      }
      reader.readAsText(file)
    },
    submitSignatories () {
      if (!this.allValid) return
      const updated = this.localSubmitters.map((s) => ({
        uuid: s.uuid,
        name: [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.name || s.email,
        first_name: s.first_name,
        last_name: s.last_name,
        email: s.email.trim(),
        order: s.order
      }))
      this.$emit('save-signatories', updated)
    }
  }
}
</script>
